import { z } from 'zod';
import { JEWEL_SOCKET_MODES, JEWEL_VISUAL_SHAPES } from './jewels';

/**
 * O que o runtime sabe sobre a ARTE de uma joia (gate V28B.1).
 *
 * Uma projeção gerada dos masters, escrita pelo pipeline e lida por quem
 * desenha. Liga, por definição: a chave de arte, o hash do master, o hash do
 * derivado, a dimensão, a forma visual, o modo de socket e o caminho servido.
 *
 * ## O que este contrato não pode carregar
 *
 * Efeito, magnitude, chance, custo ou economia. Não há campo onde coubessem, e
 * isso é a garantia: **nada de jogo é inferido de uma imagem**. O que uma joia
 * FAZ vive em `jewels.ts`, decidido por pessoas, e hoje só a peça soberana tem
 * um efeito - as catorze de socket estão em rascunho.
 *
 * ## A máscara existe para todas
 *
 * Foi opcional enquanto a única coisa que a consumia era o contorno: uma esfera
 * tem um aro circular trivial, e só a silhueta precisava de ser recortada. Isso
 * mudou quando a luz passou a viver DENTRO da joia - um brilho interno tem de
 * acabar exatamente onde a arte acaba, e "exatamente" é a alpha do derivado.
 * O tipo mantém-se anulável porque uma arte futura pode não ter máscara, e um
 * `null` obriga quem lê a decidir o que fazer em vez de assumir.
 */

export const jewelManifestEntrySchema = z
  .object({
    definitionId: z.string().min(1),
    /** A chave de arte. É o id: uma joia tem uma arte e uma arte só. */
    assetKey: z.string().min(1),
    socketMode: z.enum(JEWEL_SOCKET_MODES),
    visualShape: z.enum(JEWEL_VISUAL_SHAPES),
    /** O ficheiro autorado, sob `assets-source/jewels/`. */
    master: z.string().min(1),
    masterSha256: z.string().regex(/^[0-9a-f]{64}$/),
    /** O caminho servido, relativo à raiz de assets. */
    file: z.string().min(1),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    maskFile: z.string().min(1).nullable(),
    maskSha256: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .nullable(),
  })
  .strict();

export type JewelManifestEntry = z.infer<typeof jewelManifestEntrySchema>;

export const jewelManifestSchema = z
  .object({
    manifestVersion: z.number().int().min(1),
    entries: z.array(jewelManifestEntrySchema),
  })
  .strict();

export type JewelManifest = z.infer<typeof jewelManifestSchema>;
